import React, { useEffect, useRef, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  setDoc,
  deleteDoc,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { db, auth } from "../../scripts/app.js";
import Breadcrumbs from "../../common/Breadcrumbs/Breadcrumbs.js";
import { useParams, useSearchParams } from "react-router-dom";
import { DateTime } from "luxon";
import * as classes from "./taskDetails.module.css";
import { AdvancedMarker, Map, Map3D } from "@vis.gl/react-google-maps";
import { Button, Modal } from "@mui/material";

const TaskImageLightboxContext = React.createContext(null);

const colourReferences = {
  start: {
    className: "green",
    hex: "",
  },
  end: {
    className: "red",
    hex: "",
  },
  singleSelect: {
    className: "violet",
    hex: "",
  },
  photo: {
    className: "teal",
    hex: "",
  },
  issue: {
    className: "gold",
    hex: "",
  },
  picture: {
    className: "jungle-green",
    hex: "",
  },
  scan: {
    className: "mint",
    hex: "",
  },
  signature: {
    className: "hot-pink",
    hex: "",
  },
  media: {
    className: "pink",
    hex: "",
  },
  text: {
    className: "light-blue",
    hex: "",
  },
  yesNo: {
    className: "turquoise",
    hex: "",
  },
  numeric: {
    className: "purple",
    hex: "",
  },
  subjectPicker: {
    className: "fuscia",
    hex: "",
  },
  dateTime: {
    className: "orange",
    hex: "",
  },
};

function getMediaSource(value) {
  if (typeof value === "string") return value;
  return value?.url ?? value?.downloadURL ?? value?.downloadUrl ?? "";
}

function getPrimaryPhotoAnswer(answer) {
  if (!answer) return "";
  return typeof answer === "object" ? answer.photo : answer;
}

function getEventSupplementalContent(event) {
  const primaryPhotoSource = getMediaSource(getPrimaryPhotoAnswer(event.answer));
  const seenPhotoSources = new Set(primaryPhotoSource ? [primaryPhotoSource] : []);
  const additionalPhotos = [
    ...(Array.isArray(event.answer?.photos) ? event.answer.photos : []),
    ...(Array.isArray(event.images) ? event.images : []),
  ].reduce((photos, photo) => {
    const source = getMediaSource(photo);
    if (source && !seenPhotoSources.has(source)) {
      seenPhotoSources.add(source);
      photos.push(source);
    }
    return photos;
  }, []);
  const comment = [
    event.answer?.comment,
    !primaryPhotoSource ? event.answer?.caption : "",
    event.component?.comment,
  ].find(
    (value) => typeof value === "string" && value.trim().length > 0,
  );

  return {
    ...(comment && { comment: comment.trim() }),
    ...(additionalPhotos.length > 0 && { photos: additionalPhotos }),
  };
}

function hasSupplementalContent(data) {
  return Boolean(
    (typeof data?.comment === "string" && data.comment.trim()) ||
      (Array.isArray(data?.photos) && data.photos.length > 0),
  );
}

function getEventImages(event) {
  const images = [];
  const seenSources = new Set();

  const addImage = (value, alt = "Task image", signature = false) => {
    const source = getMediaSource(value);
    if (!source || seenSources.has(source)) return;

    seenSources.add(source);
    images.push({ source, alt, signature });
  };

  const addImageCollection = (
    value,
    alt = "Additional task photo",
    signature = false,
  ) => {
    if (!value) return;

    if (Array.isArray(value)) {
      value.forEach((image, index) =>
        addImage(image, `${alt} ${index + 1}`, signature),
      );
      return;
    }

    const directSource = getMediaSource(value);
    if (directSource) {
      addImage(value, alt, signature);
      return;
    }

    addImage(value.photo, alt, signature);
    addImageCollection(value.photos, alt, signature);
    addImageCollection(value.images, alt, signature);
  };

  if (event?.componentType === "photo") {
    addImage(event.answer, "Photo answer");
  } else if (
    event?.componentType === "picture" ||
    event?.componentType === "signature"
  ) {
    const isSignature = event.componentType === "signature";
    addImageCollection(
      event.answer,
      isSignature ? "Task signature" : "Task image",
      isSignature,
    );
  }

  addImageCollection(event?.commentsCaptionsPhotos);

  return images;
}

function TaskImage({ image, alt = "Task image", signature = false }) {
  const openLightbox = React.useContext(TaskImageLightboxContext);
  const source = getMediaSource(image);

  if (!source) return null;

  return (
    <button
      type="button"
      className={classes.imagePreviewButton}
      onClick={() => openLightbox?.({ source, alt, signature })}
      aria-label={`Open ${alt.toLowerCase()} in image viewer`}
      aria-haspopup="dialog"
    >
      <img
        className={`${classes.photoItem} ${
          signature ? classes.signatureItem : ""
        }`}
        src={source}
        alt={alt}
      />
      <span className={classes.imagePreviewOverlay} aria-hidden="true">
        <svg
          className={classes.imagePreviewIcon}
          viewBox="0 0 24 24"
          role="presentation"
        >
          <path d="M12,9C10.34,9 9,10.34 9,12C9,13.66 10.34,15 12,15C13.66,15 15,13.66 15,12C15,10.34 13.66,9 12,9M12,17C9.24,17 7,14.76 7,12C7,9.24 9.24,7 12,7C14.76,7 17,9.24 17,12C17,14.76 14.76,17 12,17M12,4.5C7,4.5 2.73,7.61 1,12C2.73,16.39 7,19.5 12,19.5C17,19.5 21.27,16.39 23,12C21.27,7.61 17,4.5 12,4.5Z" />
        </svg>
      </span>
    </button>
  );
}

function TaskImageLightbox({ image, onClose }) {
  const currentImage = image?.images?.[image.index];
  const canNavigate = (image?.images?.length ?? 0) > 1;
  const label = currentImage?.signature
    ? "Task signature"
    : currentImage?.alt || "Task image";

  const navigate = React.useCallback(
    (direction) => {
      if (!canNavigate) return;

      image.setIndex((currentIndex) => {
        const imageCount = image.images.length;
        return (currentIndex + direction + imageCount) % imageCount;
      });
    },
    [canNavigate, image],
  );

  useEffect(() => {
    if (!image || !canNavigate) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        navigate(-1);
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        navigate(1);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [canNavigate, image, navigate]);

  return (
    <Modal
      open={Boolean(image)}
      onClose={onClose}
      slotProps={{
        backdrop: {
          sx: { backgroundColor: "rgba(0, 0, 0, 0.86)" },
        },
      }}
    >
      <div
        className={classes.lightboxDialog}
        role="dialog"
        aria-modal="true"
        aria-label={`${label} image viewer`}
      >
        <button
          type="button"
          className={classes.lightboxCloseButton}
          onClick={onClose}
          aria-label="Close image viewer"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
          </svg>
        </button>
        {canNavigate && (
          <button
            type="button"
            className={`${classes.lightboxNavigationButton} ${classes.lightboxPreviousButton}`}
            onClick={() => navigate(-1)}
            aria-label="View previous image"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M15.41,16.59L10.83,12L15.41,7.41L14,6L8,12L14,18L15.41,16.59Z" />
            </svg>
          </button>
        )}
        {currentImage && (
          <img
            className={`${classes.lightboxImage} ${
              currentImage.signature ? classes.lightboxSignature : ""
            }`}
            src={currentImage.source}
            alt={currentImage.alt}
          />
        )}
        {canNavigate && (
          <button
            type="button"
            className={`${classes.lightboxNavigationButton} ${classes.lightboxNextButton}`}
            onClick={() => navigate(1)}
            aria-label="View next image"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M8.59,16.59L13.17,12L8.59,7.41L10,6L16,12L10,18L8.59,16.59Z" />
            </svg>
          </button>
        )}
      </div>
    </Modal>
  );
}

function PhotoAnswer({ photo, caption }) {
  const source = getMediaSource(photo);
  const normalisedCaption =
    typeof caption === "string" ? caption.trim() : "";

  if (!source) return null;

  return (
    <div className={classes.response}>
      <div className={classes.question}>A:</div>
      <figure className={`${classes.answerBody} ${classes.photoAnswer}`}>
        <TaskImage image={source} alt="Photo answer" />
        {normalisedCaption && (
          <figcaption className={classes.photoAnswerCaption}>
            {normalisedCaption}
          </figcaption>
        )}
      </figure>
    </div>
  );
}

function ProcessQuestionEvent({ data, indexPoint, setCenter, setHighlighted }) {
  console.log("ProcessQuestionEvent data", data);
  // console.log("data.commentsCaptionsPhotos.photos", data.commentsCaptionsPhotos.photos);
  const [show, setShow] = useState(false);

  let lastAccessedTime = data.processedAt;
  const handleToggle = () => {
    console.log("clicked");
    setShow((show) => !show);
  };
  return (
    <div className={classes.eventWrapper} id="${data.id}">
      <div className={classes.event + " " + classes.card}>
        <div className={classes.eventHeader}>
          <div className={classes.responseWrapper}>
            {data.questionPrompt !== null &&
            data.componentType != "scan" &&
            data.componentType != "picture" &&
            data.componentType != "issue" &&
            data.componentType != "subtask" ? (
              <div className={classes.response}>
                <div className={classes.question}>Q:</div>
                <div className={classes.answerBody}>{data.questionPrompt}</div>
              </div>
            ) : (
              ""
            )}
            {data.componentType == "issue" ? (
              <>
                <div className={classes.response}>
                  <div className={classes.question}>Issue Type:</div>
                  <div className={classes.answerBody}>
                    {data.questionPrompt}
                  </div>
                </div>
                <div className={classes.response}>
                  <div className={classes.question}>Summary:</div>
                  <div className={classes.answerBody}>{data.answer}</div>
                </div>
                <div className={classes.response}>
                  <div className={classes.question}>Description:</div>
                  <div className={classes.answerBody}>{data.description}</div>
                </div>
                <div className={classes.response}>
                  <div className={classes.question}>Focus:</div>
                  <div className={classes.answerBody}>{data.focus}</div>
                </div>
                <div className={classes.response}>
                  <div className={classes.question}>Deadline:</div>
                  <div className={classes.answerBody}>
                    {convertDate(data.deadline, "shortDateTime")}
                  </div>
                </div>
                <div className={classes.response}>
                  <div className={classes.question}>Assignee:</div>
                  <div className={classes.answerBody}>{data.assignedTo}</div>
                </div>
                {data.url ? (
                  <div className={classes.response}>
                    <div className={classes.question}>Link:</div>
                    <div className={classes.answerBody}>
                      <a href="${data.url}">{data.questionPrompt}</a>
                    </div>
                  </div>
                ) : (
                  ""
                )}
              </>
            ) : (
              ""
            )}
            {data.componentType == "scan" || data.componentType == "picture" ? (
              <div className={classes.response}>
                <div className={classes.question}>Prompt:</div>
                <div className={classes.answerBody}>{data.questionPrompt}</div>
              </div>
            ) : (
              ""
            )}
            {data.componentType == "photo" ||
            data.componentType == "signature" ||
            data.componentType == "picture" ||
            data.componentType == "scan" ||
            data.componentType == "start" ||
            data.componentType == "media" ||
            data.componentType == "issue" ||
            data.componentType == "end" ||
            data.componentType == "subtask" ? (
              ""
            ) : (
              <div className={classes.response}>
                <div className={classes.question}>A:</div>
                <div className={classes.answerBody}>{data.answer}</div>
              </div>
            )}
            {data.componentType == "media" ? (
              <div style={{ paddingTop: "1rem", borderTop: "1px solid white" }}>
                <video
                  style={{
                    width: "200px",
                    height: "200px",
                    objectFit: "cover",
                    borderRadius: "1rem",
                  }}
                  controls
                >
                  <source src={data.answer} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
            ) : (
              ""
            )}
            {data.componentType == "scan" ? (
              <div className={classes.response}>
                <div className={classes.question}>Scanned:</div>
                <div className={classes.answerBody}>{data.answer}</div>
              </div>
            ) : (
              ""
            )}

            {data.componentType == "photo" ? (
              <PhotoAnswer photo={data.answer} caption={data.answerCaption} />
            ) : data.componentType == "picture" ||
              data.componentType == "signature" ? (
              <div>
                {commentsCaptionsPhotos(data.answer, data.componentType)}
              </div>
            ) : (
              // <></>
              ""
            )}
            {data.componentType == "subtask" ? (
              <div className={classes.response}>
                <div className={classes.question}>Workflow:</div>
                <div>{data.questionPrompt}</div>
              </div>
            ) : (
              ""
            )}
            {data.componentType == "start" ? (
              <div className={classes.response}>
                <div className={classes.question}>Started by:</div>
                <div>{data.startedBy}</div>
              </div>
            ) : (
              ""
            )}
            {data.componentType == "start" ? (
              <div className={classes.response}>
                <div className={classes.question}>Started at:</div>
                <div>{data.startedAt}</div>
              </div>
            ) : (
              ""
            )}

            {data.componentType == "end" ? (
              <div className={classes.response}>
                <div className={classes.question}>Ended by:</div>
                <div>{data.userName}</div>
              </div>
            ) : (
              ""
            )}
            {data.componentType == "end" ? (
              <div className={classes.response}>
                <div className={classes.question}>Ended at:</div>
                <div>{convertDate(data.processedAt, "shortDateTime")}</div>
              </div>
            ) : (
              ""
            )}

            <div className={classes.smallDate}>
              {convertDate(data.processedAt, "shortDateTime")}
            </div>
            {hasSupplementalContent(data.commentsCaptionsPhotos) ? (
              <div>{commentsCaptionsPhotos(data.commentsCaptionsPhotos)}</div>
            ) : (
              // <></>
              ""
            )}
          </div>
          {data.componentType == "subtask" ? 
              <Button
                variant="contained"
                className={classes.print}
                href={data.answer}
                sx={{ backgroundColor: "#4f7b7b", borderRadius: 20, height: 40, display: "flex", textWrap: "nowrap" }}
              >
                View Detail
              </Button> :
            <div className={`${classes.print} ${classes.chevronWrapper}`} onClick={handleToggle}>
            <svg
              className={classes.chevron}
              width="40px"
              height="40px"
              version="1.1"
              viewBox="0 0 1200 1200"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill="#3b3630;"
                d="m892.36 676.36-432 432c-21.094 21.098-48.719 31.645-76.355 31.645s-55.262-10.547-76.355-31.645c-42.191-42.156-42.191-110.57 0-152.72l355.63-355.63-355.63-355.64c-42.191-42.156-42.191-110.57 0-152.72 42.191-42.191 110.53-42.191 152.72 0l432 432c42.18 42.156 42.18 110.58-0.011719 152.72z"
              />
            </svg>
          </div>}
        </div>
        <div
          className={`${classes.detailsWrapper} ${show ? classes.showDetails : classes.hideDetails}`}
        >
          <div>
            <span>User:</span> {data.userName}
          </div>
          <div>
            <span>Time:</span> {convertDate(data.processedAt, "shortDateTime")}
          </div>
          <div>
            <span>Location:</span> {(data.latitude == 0 || data.latitude == "") && (data.longitude == 0 || data.longitude == "") ? "No location provided" : `${data.latitude}, ${data.longitude}`}
          </div>
          <div>
            <span>Device:</span> {data.deviceManufacturer}, {data.deviceModel}
          </div>
          <div>
            <span>Device Info:</span> {data.devicePlatform}:{" "}
            {data.deviceVersion}
          </div>
          <div>
            <span>App Version:</span> {data.appVersion}
          </div>
        </div>
      </div>
      <div className={classes.eventAux}>
        <div className={classes.eventTime}>
          {convertDate(data.processedAt, "timeOnly")}
        </div>
        <div
          className={
            classes.eventIcon + " " + classes[data.componentClass + "Colour"] + " " + classes.print
          }
          onClick={() => {
            console.log("icon  clicked indexpoint: ", indexPoint);
            if (data.latitude && data.longitude) {
              setCenter({ lat: data.latitude, lng: data.longitude });
              setHighlighted(indexPoint);
            } else {
              setCenter({ lat: 0, lng: 0 });
            }
          }}
        >
          <svg
            className={classes.geolocation}
            width="18px"
            height="18px"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
          >
            <path
              fill="#fff;"
              d="M12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5M12,2A7,7 0 0,0 5,9C5,14.25 12,22 12,22C12,22 19,14.25 19,9A7,7 0 0,0 12,2Z"
            />
          </svg>
          {handleComponentIcons(data.componentType)}
          <div className={classes.iconLable}>{data.componentType}</div>
        </div>
      </div>
    </div>
  );
}

function convertDate(date, format) {
  switch (format) {
    case "fullDate":
      return DateTime.fromSeconds(date).toFormat("DDDD");
      break;
    case "shortDateOnly":
      return DateTime.fromSeconds(date).toFormat("DD");
      break;
    case "shortDateTime":
      return DateTime.fromSeconds(date).toFormat("DD HH:mm:ss ");
      break;
    case "timeOnly":
      return DateTime.fromSeconds(date).toFormat("HH:mm:ss ");
      break;
    case "relative":
      let lastDateTime = DateTime.fromSeconds(date);

      const now = DateTime.local(); // Current DateTime

      const diff = now
        .diff(lastDateTime, ["months", "days", "hours", "minutes", "seconds"])
        .shiftTo("months", "days", "hours", "minutes", "seconds");
      const formatString = diff
        .toFormat(
          "M 'months', d 'days', h 'hours', m 'minutes', s 'seconds' ago",
        )
        .split(",")
        // .filter(str => !str.includes('0'))
        .join(", ");

      const formattedDiff = `${formatString}`;
      return formattedDiff;
    default:
      return DateTime.fromSeconds(date).toFormat("HH:mm DD");
  }
}

function StartEvent({
  data,
  indexPoint,
  tasksWorkflowName,
  setCenter,
  setHighlighted,
}) {
  // if (data.location != undefined) {
  //   startLatitude = parseFloat(data.location.latitude);
  //   startLongitude = parseFloat(data.location.longitude);
  // }

  // if (startCreated == false) {
  //   initMap();
  //   startCreated = true;
  // }

  const markerLatLng = {
    lat: data.location != undefined ? data.location.latitude : 0,
    lng: data.location != undefined ? data.location.longitude : 0,
  };
  // useEffect(() => {
  //   let ignore = false;
  //   if (!ignore) {
  //     setCenter(markerLatLng)
  //   }
  //   return () => {
  //     ignore = true;
  //   }
  // }, [])
  console.log("data in start event", data); // this log

  // const questionEvent = {
  //   componentType: data.component.type,
  //   componentClass: "green",
  //   id: data.component.id,
  //   questionPrompt: null,
  //   // answer: data.answer.text,
  //   commentsCaptionsPhotos: {},
  //   startedAt: convertDate(data.processedAt.seconds, "shortDateTime"),
  //   startedBy: data.user.name,
  //   processedAt: data.processedAt.seconds,
  //   userName: data.user.name,
  //   latitude: data.location != undefined ? data.location.latitude : "",
  //   longitude: data.location != undefined ? data.location.longitude : "",
  //   deviceManufacturer: data.device.manufacturer,
  //   deviceModel: data.device.model,
  //   devicePlatform: data.device.platform,
  //   deviceVersion: data.device.version,
  //   appVersion: data.device.appVersion,
  // };

  // addEventMarker(markerLatLng, indexPoint, questionEvent);

  // let workflowName = document.getElementById("workflow-name");
  // workflowName.innerText = tasksWorkflowName;

  return (
    <div className={classes.start}>
      {
        <ProcessQuestionEvent
          data={data}
          indexPoint={indexPoint}
          setCenter={setCenter}
          setHighlighted={setHighlighted}
        />
      }
    </div>
  );
}

function EndEvent({ data, indexPoint, setCenter, setHighlighted }) {
  // endState = true;

  // taskStatus.classList.remove("task-status-live");
  // taskStatusIndicator.style.display = "none";
  // taskStatus.innerText = "Task Complete";

  let markerLatLng = {
    latitude: data.location != undefined ? data.location.latitude : "",
    longitude: data.location != undefined ? data.location.longitude : "",
  };

  // const questionEvent = {
  //   componentType: data.component.type,
  //   componentClass: "red",
  //   id: data.component.id,
  //   questionPrompt: null,
  //   commentsCaptionsPhotos: {},
  //   startedAt: convertDate(data.processedAt.seconds, "shortDateTime"),
  //   startedBy: data.user.name,
  //   processedAt: data.processedAt.seconds,
  //   userName: data.user.name,
  //   latitude: data.location != undefined ? data.location.latitude : "",
  //   longitude: data.location != undefined ? data.location.longitude : "",
  //   deviceManufacturer: data.device.manufacturer,
  //   deviceModel: data.device.model,
  //   devicePlatform: data.device.platform,
  //   deviceVersion: data.device.version,
  //   appVersion: data.device.appVersion,
  // };

  // addEventMarker(markerLatLng, indexPoint, questionEvent);

  return (
    <div className={classes.end}>
      {
        <ProcessQuestionEvent
          data={data}
          indexPoint={indexPoint}
          setCenter={setCenter}
          setHighlighted={setHighlighted}
        />
      }
    </div>
  );
}

function YesNoEvent({ data, indexPoint, setCenter, setHighlighted }) {
  // const questionEvent = {
  //   componentType: data.component.type,
  //   componentClass: "turquoise",
  //   id: data.component.id,
  //   questionPrompt: data.component.question,
  //   answer: data.answer.text,
  //   commentsCaptionsPhotos: data.answer,
  //   processedAt: data.processedAt.seconds,
  //   userName: data.user.name,
  //   latitude: data.location != undefined ? data.location.latitude : "",
  //   longitude: data.location != undefined ? data.location.longitude : "",
  //   deviceManufacturer: data.device.manufacturer,
  //   deviceModel: data.device.model,
  //   devicePlatform: data.device.platform,
  //   deviceVersion: data.device.version,
  //   appVersion: data.device.appVersion,
  // };

  let markerLatLng = {
    latitude: data.location != undefined ? data.location.latitude : "",
    longitude: data.location != undefined ? data.location.longitude : "",
  };

  // addEventMarker(markerLatLng, indexPoint, questionEvent);

  return (
    <ProcessQuestionEvent
      data={data}
      indexPoint={indexPoint}
      setCenter={setCenter}
      setHighlighted={setHighlighted}
    />
  );
}

function NumericEvent({ data, indexPoint, setCenter, setHighlighted }) {
  // const questionEvent = {
  //   componentType: data.component.type,
  //   componentClass: "purple",
  //   id: data.component.id,
  //   questionPrompt: data.component.question,
  //   answer: data.answer.number, // or null
  //   commentsCaptionsPhotos: data.answer,
  //   processedAt: data.processedAt.seconds,
  //   userName: data.user.name,
  //   latitude: data.location ? data.location.latitude : 0,
  //   longitude: data.location ? data.location.longitude : 0,
  //   deviceManufacturer: data.device.manufacturer,
  //   deviceModel: data.device.model,
  //   devicePlatform: data.device.platform,
  //   deviceVersion: data.device.version,
  //   appVersion: data.device.appVersion,
  // };

  let markerLatLng = {
    latitude: data.location != undefined ? data.location.latitude : "",
    longitude: data.location != undefined ? data.location.longitude : "",
  };

  // addEventMarker(markerLatLng, indexPoint, questionEvent);
  return (
    <ProcessQuestionEvent
      data={data}
      indexPoint={indexPoint}
      setCenter={setCenter}
      setHighlighted={setHighlighted}
    />
  );
}

function SubjectEvent({ data, indexPoint, setCenter, setHighlighted }) {
  // const questionEvent = {
  //   componentType: data.component.type,
  //   componentClass: "gold",
  //   id: data.component.id,
  //   questionPrompt: "Subject selected",
  //   answer: data.answer.subject.name, // or null
  //   commentsCaptionsPhotos: data.answer,
  //   processedAt: data.processedAt.seconds,
  //   userName: data.user.name,
  //   latitude: data.location ? data.location.latitude : 0,
  //   longitude: data.location ? data.location.longitude : 0,
  //   deviceManufacturer: data.device.manufacturer,
  //   deviceModel: data.device.model,
  //   devicePlatform: data.device.platform,
  //   deviceVersion: data.device.version,
  //   appVersion: data.device.appVersion,
  // };

  let markerLatLng = {
    latitude: data.location ? data.location.latitude : 0,
    longitude: data.location ? data.location.longitude : 0,
  };

  // addEventMarker(markerLatLng, indexPoint, questionEvent);

  return (
    <ProcessQuestionEvent
      data={data}
      indexPoint={indexPoint}
      setCenter={setCenter}
      setHighlighted={setHighlighted}
    />
  );
}

function SingleSelectEvent({ data, indexPoint, setCenter, setHighlighted }) {
  // const questionEvent = {
  //   componentType: data.component.type,
  //   componentClass: "violet",
  //   id: data.component.id,
  //   questionPrompt: data.component.question,
  //   answer: data.answer.text, // or null
  //   commentsCaptionsPhotos: data.answer,
  //   processedAt: data.processedAt.seconds,
  //   userName: data.user.name,
  //   latitude: data.location != undefined ? data.location.latitude : "",
  //   longitude: data.location != undefined ? data.location.longitude : "",
  //   deviceManufacturer: data.device.manufacturer,
  //   deviceModel: data.device.model,
  //   devicePlatform: data.device.platform,
  //   deviceVersion: data.device.version,
  //   appVersion: data.device.appVersion,
  // };

  let markerLatLng = {
    latitude: data.location != undefined ? data.location.latitude : "",
    longitude: data.location != undefined ? data.location.longitude : "",
  };

  // addEventMarker(markerLatLng, indexPoint, questionEvent);
  return (
    <ProcessQuestionEvent
      data={data}
      indexPoint={indexPoint}
      setCenter={setCenter}
      setHighlighted={setHighlighted}
    />
  );
}

function IssueEvent({ data, indexPoint, setCenter, setHighlighted }) {
  // let unProcessedImageArray = [];
  // let processedImageArray = [];

  // if (data.issue.images) {
  //   unProcessedImageArray = data.issue.images;
  //   processedImageArray = [];

  //   unProcessedImageArray.forEach((image) => {
  //     processedImageArray.push(image.url);
  //   });
  // }

  // let url = data.issue.url ? data.issue.url : "";

  // console.log("processedImageArray", processedImageArray);

  // const questionEvent = {
  //   componentType: data.component.type,
  //   componentClass: "fuscia",
  //   id: data.component.id,
  //   questionPrompt: data.component.issueType.name,
  //   answer: data.issue.summary, // or null
  //   assignedTo: data.issue.assigneeTeam.name,
  //   deadline: data.issue.deadline.seconds,
  //   description: data.issue.description,
  //   focus: data.issue.focus.name,
  //   issueURL: url,
  //   commentsCaptionsPhotos: { photos: processedImageArray },
  //   processedAt: data.processedAt.seconds,
  //   userName: data.user.name,
  //   latitude: data.location != undefined ? data.location.latitude : "",
  //   longitude: data.location != undefined ? data.location.longitude : "",
  //   deviceManufacturer: data.device.manufacturer,
  //   deviceModel: data.device.model,
  //   devicePlatform: data.device.platform,
  //   deviceVersion: data.device.version,
  //   appVersion: data.device.appVersion,
  // };

  let markerLatLng = {
    latitude: data.location != undefined ? data.location.latitude : "",
    longitude: data.location != undefined ? data.location.longitude : "",
  };

  // addEventMarker(markerLatLng, indexPoint, questionEvent);
  return (
    <ProcessQuestionEvent
      data={data}
      indexPoint={indexPoint}
      setCenter={setCenter}
      setHighlighted={setHighlighted}
    />
  );
}

function PhotoEvent({ data, indexPoint, setCenter, setHighlighted }) {
  // const questionEvent = {
  //   componentType: data.component.type,
  //   componentClass: "teal",
  //   id: data.component.id,
  //   questionPrompt: data.component.prompt,
  //   answer: data.answer, // or null
  //   commentsCaptionsPhotos: data.answer,
  //   processedAt: data.processedAt.seconds,
  //   userName: data.user.name,
  //   latitude: data.location != undefined ? data.location.latitude : "",
  //   longitude: data.location != undefined ? data.location.longitude : "",
  //   deviceManufacturer: data.device.manufacturer,
  //   deviceModel: data.device.model,
  //   devicePlatform: data.device.platform,
  //   deviceVersion: data.device.version,
  //   appVersion: data.device.appVersion,
  // };

  let markerLatLng = {
    latitude: data.location != undefined ? data.location.latitude : "",
    longitude: data.location != undefined ? data.location.longitude : "",
  };

  // addEventMarker(markerLatLng, indexPoint, questionEvent);
  return (
    <ProcessQuestionEvent
      data={data}
      indexPoint={indexPoint}
      setCenter={setCenter}
      setHighlighted={setHighlighted}
    />
  );
}

function SignatureEvent({ data, indexPoint, setCenter, setHighlighted }) {
  console.log("signature event: ", data);

  // const questionEvent = {
  //   componentType: data.component.type,
  //   componentClass: "hot-pink",
  //   id: data.component.id,
  //   questionPrompt: data.component.prompt,
  //   answer: data.answer.signature, // or null
  //   commentsCaptionsPhotos: data.answer.signature,
  //   processedAt: data.processedAt.seconds,
  //   userName: data.user.name,
  //   latitude: data.location != undefined ? data.location.latitude : "",
  //   longitude: data.location != undefined ? data.location.longitude : "",
  //   deviceManufacturer: data.device.manufacturer,
  //   deviceModel: data.device.model,
  //   devicePlatform: data.device.platform,
  //   deviceVersion: data.device.version,
  //   appVersion: data.device.appVersion,
  // };

  let markerLatLng = {
    latitude: data.location != undefined ? data.location.latitude : "",
    longitude: data.location != undefined ? data.location.longitude : "",
  };

  // addEventMarker(markerLatLng, indexPoint, questionEvent);
  return (
    <ProcessQuestionEvent
      data={data}
      indexPoint={indexPoint}
      setCenter={setCenter}
      setHighlighted={setHighlighted}
    />
  );
}

function ScanEvent({ data, indexPoint, setCenter, setHighlighted }) {
  // const questionEvent = {
  //   componentType: data.component.type,
  //   componentClass: "mint",
  //   id: data.component.id,
  //   questionPrompt: data.component.prompt,
  //   answer: data.answer.subject.name, // or null
  //   commentsCaptionsPhotos: data.answer,
  //   processedAt: data.processedAt.seconds,
  //   userName: data.user.name,
  //   latitude: data.location != undefined ? data.location.latitude : "",
  //   longitude: data.location != undefined ? data.location.longitude : "",
  //   deviceManufacturer: data.device.manufacturer,
  //   deviceModel: data.device.model,
  //   devicePlatform: data.device.platform,
  //   deviceVersion: data.device.version,
  //   appVersion: data.device.appVersion,
  // };

  let markerLatLng = {
    latitude: data.location != undefined ? data.location.latitude : "",
    longitude: data.location != undefined ? data.location.longitude : "",
  };

  // addEventMarker(markerLatLng, indexPoint, questionEvent);
  return (
    <ProcessQuestionEvent
      data={data}
      indexPoint={indexPoint}
      setCenter={setCenter}
      setHighlighted={setHighlighted}
    />
  );
}

function PictureEvent({ data, indexPoint, setCenter, setHighlighted }) {
  // const questionEvent = {
  //   componentType: data.component.type,
  //   componentClass: "jungle-green",
  //   id: data.component.id,
  //   questionPrompt: data.component.caption,
  //   answer: data.component.leadPicture,
  //   // commentsCaptionsPhotos: data.answer,
  //   commentsCaptionsPhotos: { photo: [data.component.leadPicture] },
  //   processedAt: data.processedAt.seconds,
  //   userName: data.user.name,
  //   latitude: data.location.latitude,
  //   longitude: data.location.longitude,
  //   deviceManufacturer: data.device.manufacturer,
  //   deviceModel: data.device.model,
  //   devicePlatform: data.device.platform,
  //   deviceVersion: data.device.version,
  //   appVersion: data.device.appVersion,
  // };

  let markerLatLng = {
    latitude: data.location != undefined ? data.location.latitude : "",
    longitude: data.location != undefined ? data.location.longitude : "",
  };

  // addEventMarker(markerLatLng, indexPoint, questionEvent);
  return (
    <ProcessQuestionEvent
      data={questionEvent}
      indexPoint={indexPoint}
      setCenter={setCenter}
      setHighlighted={setHighlighted}
    />
  );
}

function MediaEvent({ data, indexPoint, setCenter, setHighlighted }) {
  // const questionEvent = {
  //   componentType: data.component.type,
  //   componentClass: "pink",
  //   id: data.component.id,
  //   questionPrompt: data.component.prompt,
  //   answer: data.answer.video,
  //   commentsCaptionsPhotos: data.answer,
  //   processedAt: data.processedAt.seconds,
  //   userName: data.user.name,
  //   latitude: data.location != undefined ? data.location.latitude : "",
  //   longitude: data.location != undefined ? data.location.longitude : "",
  //   deviceManufacturer: data.device.manufacturer,
  //   deviceModel: data.device.model,
  //   devicePlatform: data.device.platform,
  //   deviceVersion: data.device.version,
  //   appVersion: data.device.appVersion,
  // };

  //   console.log("Media component processed", questionEvent);

  let markerLatLng = {
    latitude: data.location != undefined ? data.location.latitude : "",
    longitude: data.location != undefined ? data.location.longitude : "",
  };

  // addEventMarker(markerLatLng, indexPoint, questionEvent);
  return (
    <ProcessQuestionEvent
      data={data}
      indexPoint={indexPoint}
      setCenter={setCenter}
      setHighlighted={setHighlighted}
    />
  );
}

// function multiSelectEvent({data, indexPoint}) {

//   let multiSelectItemsString = ""

//    console.log("multi data", data)

//   data.answer.options.forEach((item, index) => {
//     console.log("index", index)
//     if (index == 0) {
//         multiSelectItemsString = multiSelectItemsString + item.text;
//     } else if (index !== (data.answer.options.length)) {
//         multiSelectItemsString = multiSelectItemsString + ", " + item.text;
//     } else {
//         multiSelectItemsString = multiSelectItemsString + item.text;
//     }
// })

//   const questionEvent = {
//     componentType: data.component.type,
//     componentClass: "orange",
//     id: data.component.id,
//     questionPrompt: data.component.question,
//     answer: multiSelectItemsString,
//     commentsCaptionsPhotos: data.answer,
//     processedAt: data.processedAt.seconds,
//     userName: data.user.name,
//     latitude: data.location != undefined ? data.location.latitude : "",
//     longitude: data.location != undefined ? data.location.longitude : "",
//     deviceManufacturer: data.device.manufacturer,
//     deviceModel: data.device.model,
//     devicePlatform: data.device.platform,
//     deviceVersion: data.device.version,
//     appVersion: data.device.appVersion,
//   };

//   let markerLatLng = {
//     latitude: data.location != undefined ? data.location.latitude : "",
//     longitude: data.location != undefined ? data.location.longitude : "",
//   };

//   addEventMarker(markerLatLng, indexPoint, questionEvent);
//   return <ProcessQuestionEvent data={questionEvent} indexPoint/>;
// }

function TextEvent({ data, indexPoint, setCenter, setHighlighted }) {
  // const questionEvent = {
  //   componentType: data.component.type,
  //   componentClass: "light-blue",
  //   id: data.component.id,
  //   questionPrompt: data.component.question,
  //   answer: data.answer.text, // or null
  //   commentsCaptionsPhotos: data.answer,
  //   processedAt: data.processedAt.seconds,
  //   userName: data.user.name,
  //   latitude: data.location != undefined ? data.location.latitude : "",
  //   longitude: data.location != undefined ? data.location.longitude : "",
  //   deviceManufacturer: data.device.manufacturer,
  //   deviceModel: data.device.model,
  //   devicePlatform: data.device.platform,
  //   deviceVersion: data.device.version,
  //   appVersion: data.device.appVersion,
  // };

  let markerLatLng = {
    latitude: data.location != undefined ? data.location.latitude : "",
    longitude: data.location != undefined ? data.location.longitude : "",
  };

  // addEventMarker(markerLatLng, indexPoint, questionEvent);
  return (
    <ProcessQuestionEvent
      data={data}
      indexPoint={indexPoint}
      setCenter={setCenter}
      setHighlighted={setHighlighted}
    />
  );
}
function SubtaskEvent({ data, indexPoint, setCenter, setHighlighted }) {
  // const questionEvent = {
  //   componentType: data.component.type,
  //   componentClass: "light-blue",
  //   id: data.component.id,
  //   questionPrompt: data.component.question,
  //   answer: data.answer.text, // or null
  //   commentsCaptionsPhotos: data.answer,
  //   processedAt: data.processedAt.seconds,
  //   userName: data.user.name,
  //   latitude: data.location != undefined ? data.location.latitude : "",
  //   longitude: data.location != undefined ? data.location.longitude : "",
  //   deviceManufacturer: data.device.manufacturer,
  //   deviceModel: data.device.model,
  //   devicePlatform: data.device.platform,
  //   deviceVersion: data.device.version,
  //   appVersion: data.device.appVersion,
  // };

  let markerLatLng = {
    latitude: data.location != undefined ? data.location.latitude : "",
    longitude: data.location != undefined ? data.location.longitude : "",
  };

  // addEventMarker(markerLatLng, indexPoint, questionEvent);
  return (
    <ProcessQuestionEvent
      data={data}
      indexPoint={indexPoint}
      setCenter={setCenter}
      setHighlighted={setHighlighted}
    />
  );
}

function DateTimeEvent({ data, indexPoint, setCenter, setHighlighted }) {
  // console.log("data.answer", data.answer.dateTimeAt.seconds);

  // let dateAnswer;

  // if (data.answer.timeAt) {
  //   dateAnswer = convertDate(data.answer.timeAt.seconds, "shortDateTime");
  //   console.log("data.answer.dateTimeAt", data.answer);
  // } else if (data.answer.dateAt) {
  //   dateAnswer = convertDate(data.answer.dateAt.seconds, "shortDateOnly");
  //   console.log("data.answer.dateAt", data.answer.dateAt);
  // } else if (data.answer.duration) {
  //   dateAnswer = data.answer.duration;
  // } else {
  //   dateAnswer = "";
  // }

  // console.log("dateAnswer", dateAnswer);

  // const questionEvent = {
  //   componentType: data.component.type,
  //   componentClass: "orange",
  //   id: data.component.id,
  //   questionPrompt: data.component.question,
  //   answer: dateAnswer,
  //   commentsCaptionsPhotos: data.answer,
  //   processedAt: data.processedAt.seconds,
  //   userName: data.user.name,
  //   latitude: data.location != undefined ? data.location.latitude : "",
  //   longitude: data.location != undefined ? data.location.longitude : "",
  //   deviceManufacturer: data.device.manufacturer,
  //   deviceModel: data.device.model,
  //   devicePlatform: data.device.platform,
  //   deviceVersion: data.device.version,
  //   appVersion: data.device.appVersion,
  // };

  // let markerLatLng = {
  //   latitude: data.location != undefined ? data.location.latitude : "",
  //   longitude: data.location != undefined ? data.location.longitude : "",
  // };

  // addEventMarker(markerLatLng, indexPoint, questionEvent);
  return (
    <ProcessQuestionEvent
      data={data}
      indexPoint={indexPoint}
      setCenter={setCenter}
      setHighlighted={setHighlighted}
    />
  );
}

function commentsCaptionsPhotos(data, imageType = "photo") {
  console.log("photo OR SIGNATURE data", data);

  if (!data) return "";

  const renderPhotos = (photos) =>
    photos.map((photo, index) => {
      const source = getMediaSource(photo);
      if (!source) return null;

      return (
        <TaskImage
          key={`${source}-${index}`}
          image={source}
          alt={`Additional task photo ${index + 1}`}
        />
      );
    });

  if (data.comment && !data.photos) {
    return (
      <div className={classes.commentsPhotos}>
        <div className={classes.comment}>
          COMMENT
          <div className={classes.response}>{data.comment}</div>
        </div>
      </div>
    );
  }

  if (!data.photo && !data.photos) {
    const isSignature = imageType === "signature";

    return (
      <div className={classes.commentsPhotos}>
        <ul className={`${classes.photoList} ${classes.noComment}`}>
          <TaskImage
            image={data}
            alt={isSignature ? "Task signature" : "Task image"}
            signature={isSignature}
          />
        </ul>
      </div>
    );
  }

  if (data.photo && !data.caption) {
    return (
      <div className={classes.commentsPhotos}>
        <ul className={classes.photoList}>
          <TaskImage image={data.photo} alt="Task photo" />
        </ul>
      </div>
    );
  } else if (data.photo && data.caption) {
    console.log("caption minfo ++++++++", data.caption);

    return (
      <div className={classes.commentsPhotos}>
        <div className={classes.comment}>
          CAPTION
          <div className={classes.response}>{data.caption}</div>
        </div>
        <ul className={classes.photoList}>
          <TaskImage image={data.photo} alt="Task photo" />
        </ul>
      </div>
    );
  } else if (data.comment && data.photos) {
    console.log("In data .photos and comments:", data);

    return (
      <div className={classes.commentsPhotos}>
        <div className={classes.comment}>
          COMMENT
          <div className={classes.response}>{data.comment}</div>
        </div>
        <ul className={classes.photoList}>{renderPhotos(data.photos)}</ul>
      </div>
    );
  } else if (data.photos) {
    return (
      <div className={classes.commentsPhotos}>
        <ul className={classes.photoList + " " + classes.noComment}>
          {renderPhotos(data.photos)}
        </ul>
      </div>
    );
  } else {
    return "";
  }
}

function handleComponentIcons(componentType) {
  switch (componentType) {
    case "start":
      return (
        <svg
          width="18px"
          height="18px"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="#ffffff"
        >
          <path d="M12,1C8.14,1 5,4.14 5,8A7,7 0 0,0 12,15C15.86,15 19,11.87 19,8C19,4.14 15.86,1 12,1M12,3.15C14.67,3.15 16.85,5.32 16.85,8C16.85,10.68 14.67,12.85 12,12.85A4.85,4.85 0 0,1 7.15,8A4.85,4.85 0 0,1 12,3.15M11,5V8.69L14.19,10.53L14.94,9.23L12.5,7.82V5M4,16V24H6V21H18V24L22,20L18,16V19H6V16" />
        </svg>
      );
    case "singleSelect":
      return (
        <svg
          fill="#ffffff"
          width="22"
          height="22"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
        >
          <path d="M7,5H21V7H7V5M7,13V11H21V13H7M4,4.5A1.5,1.5 0 0,1 5.5,6A1.5,1.5 0 0,1 4,7.5A1.5,1.5 0 0,1 2.5,6A1.5,1.5 0 0,1 4,4.5M4,10.5A1.5,1.5 0 0,1 5.5,12A1.5,1.5 0 0,1 4,13.5A1.5,1.5 0 0,1 2.5,12A1.5,1.5 0 0,1 4,10.5M7,19V17H21V19H7M4,16.5A1.5,1.5 0 0,1 5.5,18A1.5,1.5 0 0,1 4,19.5A1.5,1.5 0 0,1 2.5,18A1.5,1.5 0 0,1 4,16.5Z" />
        </svg>
      );
    case "text":
      return (
        <svg
          fill="#ffffff"
          width="22"
          height="22"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
        >
          <path d="M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z" />
        </svg>
      );
    case "dateTime":
      return (
        <svg
          fill="#ffffff"
          width="22"
          height="22"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
        >
          <path d="M6 1V3H5C3.89 3 3 3.89 3 5V19C3 20.1 3.89 21 5 21H11.1C12.36 22.24 14.09 23 16 23C19.87 23 23 19.87 23 16C23 14.09 22.24 12.36 21 11.1V5C21 3.9 20.11 3 19 3H18V1H16V3H8V1M5 5H19V7H5M5 9H19V9.67C18.09 9.24 17.07 9 16 9C12.13 9 9 12.13 9 16C9 17.07 9.24 18.09 9.67 19H5M16 11.15C18.68 11.15 20.85 13.32 20.85 16C20.85 18.68 18.68 20.85 16 20.85C13.32 20.85 11.15 18.68 11.15 16C11.15 13.32 13.32 11.15 16 11.15M15 13V16.69L18.19 18.53L18.94 17.23L16.5 15.82V13Z" />
        </svg>
      );
    case "photo":
      return (
        <svg
          fill="#ffffff"
          width="22"
          height="22"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
        >
          <path d="M4,4H7L9,2H15L17,4H20A2,2 0 0,1 22,6V18A2,2 0 0,1 20,20H4A2,2 0 0,1 2,18V6A2,2 0 0,1 4,4M12,7A5,5 0 0,0 7,12A5,5 0 0,0 12,17A5,5 0 0,0 17,12A5,5 0 0,0 12,7M12,9A3,3 0 0,1 15,12A3,3 0 0,1 12,15A3,3 0 0,1 9,12A3,3 0 0,1 12,9Z" />
        </svg>
      );
    case "signature":
      return (
        <svg fill="#ffffff" width="22" viewBox="0 0 24 24">
          <path d="M9.75 20.85C11.53 20.15 11.14 18.22 10.24 17C9.35 15.75 8.12 14.89 6.88 14.06C6 13.5 5.19 12.8 4.54 12C4.26 11.67 3.69 11.06 4.27 10.94C4.86 10.82 5.88 11.4 6.4 11.62C7.31 12 8.21 12.44 9.05 12.96L10.06 11.26C8.5 10.23 6.5 9.32 4.64 9.05C3.58 8.89 2.46 9.11 2.1 10.26C1.78 11.25 2.29 12.25 2.87 13.03C4.24 14.86 6.37 15.74 7.96 17.32C8.3 17.65 8.71 18.04 8.91 18.5C9.12 18.94 9.07 18.97 8.6 18.97C7.36 18.97 5.81 18 4.8 17.36L3.79 19.06C5.32 20 7.88 21.47 9.75 20.85M20.84 5.25C21.06 5.03 21.06 4.67 20.84 4.46L19.54 3.16C19.33 2.95 18.97 2.95 18.76 3.16L17.74 4.18L19.82 6.26M11 10.92V13H13.08L19.23 6.85L17.15 4.77L11 10.92Z" />
        </svg>
      );
    case "issue":
      return (
        <svg width="24" fill="#ffffff" viewBox="0 0 24 24">
          <path d="M14,12H10V10H14M14,16H10V14H14M20,8H17.19C16.74,7.22 16.12,6.55 15.37,6.04L17,4.41L15.59,3L13.42,5.17C12.96,5.06 12.5,5 12,5C11.5,5 11.04,5.06 10.59,5.17L8.41,3L7,4.41L8.62,6.04C7.88,6.55 7.26,7.22 6.81,8H4V10H6.09C6.04,10.33 6,10.66 6,11V12H4V14H6V15C6,15.34 6.04,15.67 6.09,16H4V18H6.81C7.85,19.79 9.78,21 12,21C14.22,21 16.15,19.79 17.19,18H20V16H17.91C17.96,15.67 18,15.34 18,15V14H20V12H18V11C18,10.66 17.96,10.33 17.91,10H20V8Z" />
        </svg>
      );
    case "media":
      return (
        <svg
          fill="#ffffff"
          width="22"
          height="22"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
        >
          <path d="M17,10.5V7A1,1 0 0,0 16,6H4A1,1 0 0,0 3,7V17A1,1 0 0,0 4,18H16A1,1 0 0,0 17,17V13.5L21,17.5V6.5L17,10.5Z" />
        </svg>
      );
    case "subjectPicker":
      return (
        <svg
          fill="#ffffff"
          width="22"
          height="22"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
        >
          <path d="M5,15H3V19A2,2 0 0,0 5,21H9V19H5M5,5H9V3H5A2,2 0 0,0 3,5V9H5M19,3H15V5H19V9H21V5A2,2 0 0,0 19,3M19,19H15V21H19A2,2 0 0,0 21,19V15H19M12,8A4,4 0 0,0 8,12A4,4 0 0,0 12,16A4,4 0 0,0 16,12A4,4 0 0,0 12,8M12,14A2,2 0 0,1 10,12A2,2 0 0,1 12,10A2,2 0 0,1 14,12A2,2 0 0,1 12,14Z" />
        </svg>
      );
    case "yesNo":
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="#ffffff"
          width="22"
          height="22"
          viewBox="0 0 24 24"
        >
          <path d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z" />
        </svg>
      );
    case "numeric":
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="#ffffff"
          width="22"
          height="22"
          viewBox="0 0 24 24"
        >
          <path d="M4,17V9H2V7H6V17H4M22,15C22,16.11 21.1,17 20,17H16V15H20V13H18V11H20V9H16V7H20A2,2 0 0,1 22,9V10.5A1.5,1.5 0 0,1 20.5,12A1.5,1.5 0 0,1 22,13.5V15M14,15V17H8V13C8,11.89 8.9,11 10,11H12V9H8V7H12A2,2 0 0,1 14,9V11C14,12.11 13.1,13 12,13H10V15H14Z" />
        </svg>
      );
    case "end":
      return (
        <svg
          width="18px"
          height="18px"
          fill="#ffffff"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
        >
          <path d="M12,1C8.14,1 5,4.14 5,8A7,7 0 0,0 12,15C15.86,15 19,11.87 19,8C19,4.14 15.86,1 12,1M12,3.15C14.67,3.15 16.85,5.32 16.85,8C16.85,10.68 14.67,12.85 12,12.85A4.85,4.85 0 0,1 7.15,8A4.85,4.85 0 0,1 12,3.15M11,5V8.69L14.19,10.53L14.94,9.23L12.5,7.82V5M15,16V19H3V21H15V24L19,20M19,20V24H21V16H19" />
        </svg>
      );
    case "scan":
      return (
        <svg
          width="18px"
          height="18px"
          fill="#ffffff"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
        >
          <path d="M4,6H6V18H4V6M7,6H8V18H7V6M9,6H12V18H9V6M13,6H14V18H13V6M16,6H18V18H16V6M19,6H20V18H19V6M2,4V8H0V4A2,2 0 0,1 2,2H6V4H2M22,2A2,2 0 0,1 24,4V8H22V4H18V2H22M2,16V20H6V22H2A2,2 0 0,1 0,20V16H2M22,20V16H24V20A2,2 0 0,1 22,22H18V20H22Z" />
        </svg>
      );
    case "picture":
      return (
        <svg
          width="18px"
          height="18px"
          fill="#ffffff"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
        >
          <path d="M8.5,13.5L11,16.5L14.5,12L19,18H5M21,19V5C21,3.89 20.1,3 19,3H5A2,2 0 0,0 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19Z" />
        </svg>
      );
  case "subtask":
    return <svg           
          width="18px"
          height="18px"
          fill="#ffffff"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24">
            <title>briefcase</title>
            <path d="M10,2H14A2,2 0 0,1 16,4V6H20A2,2 0 0,1 22,8V19A2,2 0 0,1 20,21H4C2.89,21 2,20.1 2,19V8C2,6.89 2.89,6 4,6H8V4C8,2.89 8.89,2 10,2M14,6V4H10V6H14Z" />
          </svg>
  }
}

function BuildContent({ eventData, highlighted, indexPoint, setHighlighted }) {
  console.log("highlighted", highlighted);
  let questionPrompt;
  let answer;
  console.log("eventData: ", eventData);
  switch (eventData.componentType) {
    case "start":
      questionPrompt = "Started";
      answer = eventData.startedAt;
      break;
    case "picture":
      questionPrompt = eventData.questionPrompt;
      answer = "Picture Component";
      break;
    case "end":
      questionPrompt = "Ended";
      // answer = convertDate(eventData.processedAt ?? 0 , "shortDateTime");
      break;
    case "subtask":
      questionPrompt = eventData.questionPrompt;
      answer = ""
      break;
    default:
      questionPrompt = "Q: " + eventData.questionPrompt;
      answer = "A: " + eventData.answer;
  }

  return (
    <div
      className={`${classes.mapEventMarker} ${
        highlighted ? classes.mapEventMarkerHighlight : ""
      }`}
      onClick={() => setHighlighted(indexPoint)}
    >
      <div
        className={`${classes.mapMarkerIcon}
  ${classes[`${eventData.componentClass}Colour`]}`}
      >
        {handleComponentIcons(eventData.componentType)}
      </div>

      <div className={classes.mapMarkerDetails}>
        <div className={classes.mapMarkerQuestion}>{questionPrompt}</div>
        <div className={classes.mapMarkerAnswer}>{answer}</div>
        <div className={classes.mapMarkerFeatures}>
          <div>
            <span>User:</span>
            <span>{eventData.userName}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function evaluateMilliseconds(processedAt) {
  const milliseconds =
    processedAt.seconds * 1000 + Math.round(processedAt.nanoseconds / 1000000);
  return milliseconds;
}

function getTaskEventElementId(eventId) {
  return `task-event-${encodeURIComponent(String(eventId))}`;
}

export default function TaskDetail() {
  const { workflowID, taskID } = useParams();
  const [searchParams] = useSearchParams();
  const targetEventId = searchParams.get("eventId");
  const scrolledEventKey = useRef(null);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [taskEvents, setTaskEvents] = useState([]);
  const [tasksWorkflowName, setTasksWorkflowName] = useState("");
  const [highlighted, setHighlighted] = useState(0);
  const [center, setCenter] = useState({ lat: 0, lng: 0 });
  const [taskDocData, setTaskDocData] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [obsoleteOpen, setObsoleteOpen] = useState(false);
  const [obsoleteReason, setObsoleteReason] = useState("");
  const [obsoleting, setObsoleting] = useState(false);
  const [locatAvailable, setLocatAvailable] = useState(false);
  const [allowed, setAllowed] = useState(true);

  const openEventImage = (event, selectedImage) => {
    const eventImages = getEventImages(event);
    const images = eventImages.length > 0 ? eventImages : [selectedImage];
    const selectedIndex = images.findIndex(
      (image) => image.source === selectedImage.source,
    );

    setLightboxImage({
      images,
      index: selectedIndex >= 0 ? selectedIndex : 0,
    });
  };

  const isObsoleted = !!taskDocData?.obsoleted;
  const hasEndEvent = taskEvents.some(
    (e) =>
      e?.event?.componentType === "end" || e?.event?.component?.type === "end",
  );
  const statusText = isObsoleted
    ? "Task Obsoleted"
    : hasEndEvent
      ? "Task Complete"
      : "Task Status: Live Task";
  const onPrintTask = () => {
    setMenuOpen(false);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.print();
      });
    });
  };

  const onOpenObsolete = () => {
    setMenuOpen(false);
    setObsoleteOpen(true);
  };

  const onConfirmObsolete = async () => {
    if (!obsoleteReason.trim() || obsoleting) return;

    setObsoleting(true);
    try {
      const user = auth.currentUser;
      if (!user) throw new Error("No signed-in user");

      const usersRef = collection(db, "users");
      const q = query(usersRef, where("email", "==", user.email));
      const userSnap = await getDocs(q);
      const userDoc = userSnap.docs[0]?.data();

      const obsoleteObject = {
        at: new Date(),
        by: {
          uid: user.uid,
          id: userDoc?.id ?? "",
          email: user.email ?? "",
          firstName: userDoc?.firstName ?? "",
          surname: userDoc?.surname ?? "",
          name: userDoc?.name ?? user.email ?? "",
        },
        reason: obsoleteReason.trim(),
      };

      const taskRef = doc(db, "workflows", workflowID, "tasks", taskID);
      await setDoc(taskRef, { obsoleted: obsoleteObject }, { merge: true });

      await deleteDoc(doc(db, "liveTasks", taskID));

      setObsoleteOpen(false);
      setObsoleteReason("");
    } catch (err) {
      console.error("Failed to obsolete task:", err);
    } finally {
      setObsoleting(false);
    }
  };
  console.log(tasksWorkflowName);
  const defaultCenter = { lat: 0, lng: 0 };
  let pageBreadcrumbs = [
    { name: "Launchpad", href: "/main/launchpad" },
    {
      name: tasksWorkflowName
        ? `Task Detail: ${tasksWorkflowName}`
        : "Task Detail",
      href: "/main/launchpad/task-detail",
      current: true,
    },
  ];
  useEffect(() => {
    let availableLocations = 0;
    taskEvents.forEach((i) => {
      if (
        typeof i.event.latitude == "number" &&
        typeof i.event.longitude == "number" &&
        i.event.latitude != 0 &&
        i.event.longitude != 0
      ) {
        availableLocations += 1;
      }
    });
    if (availableLocations == 0) {
      setLocatAvailable(false);
    } else {
      setLocatAvailable(true);
    }
  }, [taskEvents]);
  useEffect(() => {
    if (!targetEventId || taskEvents.length === 0) return undefined;

    const targetKey = `${taskID}:${targetEventId}`;
    if (scrolledEventKey.current === targetKey) return undefined;

    const animationFrame = window.requestAnimationFrame(() => {
      const target = document.getElementById(
        getTaskEventElementId(targetEventId),
      );
      if (!target) return;

      target.scrollIntoView({ behavior: "smooth", block: "center" });
      scrolledEventKey.current = targetKey;
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, [targetEventId, taskEvents, taskID]);
  useEffect(() => {
    const taskRef = doc(
      collection(db, "workflows", workflowID, "tasks"),
      taskID,
    );
    const getName = async () => {
      const workflowdoc = await getDoc(
        doc(collection(db, "workflows"), workflowID),
      );

      if (workflowdoc.exists) {
        // console.log(doc.data());
        setTasksWorkflowName(workflowdoc.data().name);
      } else {
        console.log("No such workflow document!");
      }
    };

    getName().catch(function (error) {
      console.log("Error getting document:", error);
      if(error?.code === "permission-denied") {
        console.log('no permission detected');
        setAllowed(false);
      }
    });

    const unsub = onSnapshot(taskRef, (doc) => {
      console.log("Current data: ", doc.data());
      setTaskDocData(doc.data());
      let eventHTML = [];
      let indexPoint = 0;
      let questionEvent;

      const sortedArray = doc
        .data()
        .events.sort(
          (a, b) =>
            evaluateMilliseconds(a.processedAt) -
            evaluateMilliseconds(b.processedAt),
        );

      // let sortedArrayLength = sortedArray.length;

      sortedArray.forEach((event, index) => {
        const previousEventCount = eventHTML.length;
        // console.log("sortedArrayLength", sortedArrayLength, "index", index);

        switch (event.component.type) {
          case "start":
            console.log("event", event);
            questionEvent = {
              componentType: event.component.type,
              componentClass: "green",
              id: event.component.id,
              questionPrompt: null,
              // answer: event.answer.text,
              commentsCaptionsPhotos: {},
              startedAt: convertDate(
                event.processedAt.seconds,
                "shortDateTime",
              ),
              startedBy: event.user.name,
              processedAt: event.processedAt.seconds,
              userName: event.user.name,
              latitude:
                event.location != undefined ? event.location.latitude : "",
              longitude:
                event.location != undefined ? event.location.longitude : "",
              deviceManufacturer: event.device.manufacturer,
              deviceModel: event.device.model,
              devicePlatform: event.device.platform,
              deviceVersion: event.device.version,
              appVersion: event.device.appVersion,
            };
            setCenter({
              lat: event.location != undefined ? event.location.latitude : 0,
              lng: event.location != undefined ? event.location.longitude : 0,
            });
            indexPoint = indexPoint + 1;
            eventHTML.push({
              component: (
                <StartEvent
                  data={questionEvent}
                  indexPoint={indexPoint}
                  tasksWorkflowName={tasksWorkflowName}
                  setCenter={setCenter}
                  setHighlighted={setHighlighted}
                />
              ),
              indexPoint,
              event: questionEvent,
            });
            break;
          case "singleSelect":
            indexPoint = indexPoint + 1;
            console.log("singleSelectEvent: ", event);
            questionEvent = {
              componentType: event.component.type,
              componentClass: "violet",
              id: event.component.id,
              questionPrompt: event.component.question,
              answer: event.answer.text, // or null
              commentsCaptionsPhotos: getEventSupplementalContent(event),
              processedAt: event.processedAt.seconds,
              userName: event.user.name,
              latitude:
                event.location != undefined ? event.location.latitude : "",
              longitude:
                event.location != undefined ? event.location.longitude : "",
              deviceManufacturer: event.device.manufacturer,
              deviceModel: event.device.model,
              devicePlatform: event.device.platform,
              deviceVersion: event.device.version,
              appVersion: event.device.appVersion,
            };
            eventHTML.push({
              component: (
                <SingleSelectEvent
                  data={questionEvent}
                  indexPoint={indexPoint}
                  setCenter={setCenter}
                  setHighlighted={setHighlighted}
                />
              ),
              indexPoint,
              event: questionEvent,
            });
            break;
          case "subjectPicker":
            indexPoint = indexPoint + 1;
            questionEvent = {
              componentType: event.component.type,
              componentClass: "gold",
              id: event.component.id,
              questionPrompt: "Subject selected",
              answer: event.answer.subject.name, // or null
              commentsCaptionsPhotos: event.answer,
              processedAt: event.processedAt.seconds,
              userName: event.user.name,
              latitude: event.location ? event.location.latitude : 0,
              longitude: event.location ? event.location.longitude : 0,
              deviceManufacturer: event.device.manufacturer,
              deviceModel: event.device.model,
              devicePlatform: event.device.platform,
              deviceVersion: event.device.version,
              appVersion: event.device.appVersion,
            };
            eventHTML.push({
              component: (
                <SubjectEvent
                  data={questionEvent}
                  indexPoint={indexPoint}
                  setCenter={setCenter}
                  setHighlighted={setHighlighted}
                />
              ),
              indexPoint,
              event: questionEvent,
            });
            break;
          case "text":
            indexPoint = indexPoint + 1;
            questionEvent = {
              componentType: event.component.type,
              componentClass: "light-blue",
              id: event.component.id,
              questionPrompt: event.component.question,
              answer: event.answer.text, // or null
              commentsCaptionsPhotos: event.answer,
              processedAt: event.processedAt.seconds,
              userName: event.user.name,
              latitude:
                event.location != undefined ? event.location.latitude : "",
              longitude:
                event.location != undefined ? event.location.longitude : "",
              deviceManufacturer: event.device.manufacturer,
              deviceModel: event.device.model,
              devicePlatform: event.device.platform,
              deviceVersion: event.device.version,
              appVersion: event.device.appVersion,
            };
            eventHTML.push({
              component: (
                <TextEvent
                  data={questionEvent}
                  indexPoint={indexPoint}
                  setCenter={setCenter}
                  setHighlighted={setHighlighted}
                />
              ),
              indexPoint,
              event: questionEvent,
            });
            break;
          case "dateTime":
            indexPoint = indexPoint + 1;
            let dateAnswer;

            if (event.answer.timeAt) {
              dateAnswer = convertDate(event.answer.timeAt.seconds, "timeOnly");
              console.log("data.answer.dateTimeAt", event.answer);
            } else if (event.answer.dateAt) {
              dateAnswer = convertDate(
                event.answer.dateAt.seconds,
                "shortDateOnly",
              );
              console.log("data.answer.dateAt", event.answer.dateAt);
            } else if (event.answer.duration) {
              dateAnswer = event.answer.duration;
              console.log("event.answer.duration", event.answer.duration);
            } else if (event.answer.dateTimeAt) {
              dateAnswer = convertDate(
                event.answer.dateTimeAt.seconds,
                "shortDateTime",
              );
              console.log("event.answer.dateTimeAt", event.answer.dateTimeAt);
            } else if (event.answer.timeFrom && event.answer.timeTo) {
              let from = convertDate(event.answer.timeFrom.seconds, "timeOnly");
              let to = convertDate(event.answer.timeTo.seconds, "timeOnly");
              dateAnswer = `${from} - ${to}`;
            } else if (event.answer.dateFrom && event.answer.dateTo) {
              let from = convertDate(event.answer.dateFrom.seconds, "shortDateOnly");
              let to = convertDate(event.answer.dateTo.seconds, "shortDateOnly");
              dateAnswer = `${from} - ${to}`;
            } else if (event.answer.dateTimeFrom && event.answer.dateTimeTo) {
              let from = convertDate(event.answer.dateTimeFrom.seconds, "shortDateTime");
              let to = convertDate(event.answer.dateTimeTo.seconds, "shortDateTime");
              dateAnswer = `${from} - ${to}`;
            } else {
              dateAnswer = "";
            }
            questionEvent = {
              componentType: event.component.type,
              componentClass: "orange",
              id: event.component.id,
              questionPrompt: event.component.question,
              answer: dateAnswer,
              commentsCaptionsPhotos: event.answer,
              processedAt: event.processedAt.seconds,
              userName: event.user.name,
              latitude:
                event.location != undefined ? event.location.latitude : "",
              longitude:
                event.location != undefined ? event.location.longitude : "",
              deviceManufacturer: event.device.manufacturer,
              deviceModel: event.device.model,
              devicePlatform: event.device.platform,
              deviceVersion: event.device.version,
              appVersion: event.device.appVersion,
            };
            eventHTML.push({
              component: (
                <DateTimeEvent
                  data={questionEvent}
                  indexPoint={indexPoint}
                  setCenter={setCenter}
                  setHighlighted={setHighlighted}
                />
              ),
              indexPoint,
              event: questionEvent,
            });
            break;
          case "issue":
            indexPoint = indexPoint + 1;
            let unProcessedImageArray = [];
            let processedImageArray = [];

            if (event.issue.images) {
              unProcessedImageArray = event.issue.images;
              processedImageArray = [];

              unProcessedImageArray.forEach((image) => {
                processedImageArray.push(image.url);
              });
            }

            let url = event.issue.url ? event.issue.url : "";

            console.log("processedImageArray", processedImageArray);

            questionEvent = {
              componentType: event.component.type,
              componentClass: "fuscia",
              id: event.component.id,
              questionPrompt: event.component.issueType.name,
              answer: event.issue.summary, // or null
              assignedTo: event.issue.assigneeTeam.name,
              deadline: event.issue.deadline.seconds,
              description: event.issue.description,
              focus: event.issue.focus.name,
              issueURL: url,
              commentsCaptionsPhotos: { photos: processedImageArray },
              processedAt: event.processedAt.seconds,
              userName: event.user.name,
              latitude:
                event.location != undefined ? event.location.latitude : "",
              longitude:
                event.location != undefined ? event.location.longitude : "",
              deviceManufacturer: event.device.manufacturer,
              deviceModel: event.device.model,
              devicePlatform: event.device.platform,
              deviceVersion: event.device.version,
              appVersion: event.device.appVersion,
            };
            eventHTML.push({
              component: (
                <IssueEvent
                  data={questionEvent}
                  indexPoint={indexPoint}
                  setCenter={setCenter}
                  setHighlighted={setHighlighted}
                />
              ),
              indexPoint,
              event: questionEvent,
            });
            break;
          case "photo":
            indexPoint = indexPoint + 1;
            console.log("photoEvent", event);
            questionEvent = {
              componentType: event.component.type,
              componentClass: "teal",
              id: event.component.id,
              questionPrompt: event.component.prompt,
              answer: getPrimaryPhotoAnswer(event.answer),
              answerCaption: event.answer?.caption,
              commentsCaptionsPhotos: getEventSupplementalContent(event),
              processedAt: event.processedAt.seconds,
              userName: event.user.name,
              latitude:
                event.location != undefined ? event.location.latitude : "",
              longitude:
                event.location != undefined ? event.location.longitude : "",
              deviceManufacturer: event.device.manufacturer,
              deviceModel: event.device.model,
              devicePlatform: event.device.platform,
              deviceVersion: event.device.version,
              appVersion: event.device.appVersion,
            };
            eventHTML.push({
              component: (
                <PhotoEvent
                  data={questionEvent}
                  indexPoint={indexPoint}
                  setCenter={setCenter}
                  setHighlighted={setHighlighted}
                />
              ),
              indexPoint,
              event: questionEvent,
            });
            break;
          case "yesNo":
            indexPoint = indexPoint + 1;
            questionEvent = {
              componentType: event.component.type,
              componentClass: "turquoise",
              id: event.component.id,
              questionPrompt: event.component.question,
              answer: event.answer.text,
              commentsCaptionsPhotos: event.answer,
              processedAt: event.processedAt.seconds,
              userName: event.user.name,
              latitude:
                event.location != undefined ? event.location.latitude : "",
              longitude:
                event.location != undefined ? event.location.longitude : "",
              deviceManufacturer: event.device.manufacturer,
              deviceModel: event.device.model,
              devicePlatform: event.device.platform,
              deviceVersion: event.device.version,
              appVersion: event.device.appVersion,
            };
            eventHTML.push({
              component: (
                <YesNoEvent
                  data={questionEvent}
                  indexPoint={indexPoint}
                  setCenter={setCenter}
                  setHighlighted={setHighlighted}
                />
              ),
              indexPoint,
              event: questionEvent,
            });
            break;
          case "numeric":
            indexPoint = indexPoint + 1;
            questionEvent = {
              componentType: event.component.type,
              componentClass: "purple",
              id: event.component.id,
              questionPrompt: event.component.question,
              answer: event.answer.number, // or null
              commentsCaptionsPhotos: event.answer,
              processedAt: event.processedAt.seconds,
              userName: event.user.name,
              latitude: event.location ? event.location.latitude : 0,
              longitude: event.location ? event.location.longitude : 0,
              deviceManufacturer: event.device.manufacturer,
              deviceModel: event.device.model,
              devicePlatform: event.device.platform,
              deviceVersion: event.device.version,
              appVersion: event.device.appVersion,
            };
            eventHTML.push({
              component: (
                <NumericEvent
                  data={questionEvent}
                  indexPoint={indexPoint}
                  setCenter={setCenter}
                  setHighlighted={setHighlighted}
                />
              ),
              indexPoint,
              event: questionEvent,
            });
            break;
          case "decision":
            break;
          case "subtask":
            indexPoint = indexPoint + 1;
            questionEvent = {
              componentType: event.component.type,
              componentClass: "yellow",
              id: event.component.id,
              questionPrompt: event.component.name,
              answer: `/main/launchpad/task-detail/${event.component.workflowId}/${event.taskId}`, // or null
              commentsCaptionsPhotos: "",
              processedAt: event.processedAt.seconds,
              userName: event.user.name,
              latitude: event.location ? event.location.latitude : 0,
              longitude: event.location ? event.location.longitude : 0,
              deviceManufacturer: event.device.manufacturer,
              deviceModel: event.device.model,
              devicePlatform: event.device.platform,
              deviceVersion: event.device.version,
              appVersion: event.device.appVersion,
            };
            eventHTML.push({
              component: (
                <SubtaskEvent
                  data={questionEvent}
                  indexPoint={indexPoint}
                  setCenter={setCenter}
                  setHighlighted={setHighlighted}
                />
              ),
              indexPoint,
              event: questionEvent,
            });
            break;
            // console.log(`
            //           //++++++++++++++++++++++++++++++++++++
            //           catching unhandled decision events
            //           //++++++++++++++++++++++++++++++++++++`,
            //   event,
            //   indexPoint
            // );

            // eventHTML = eventHTML.push((processOtherEvent(event, indexPoint)));
            break;
          case "hub":
            // console.log(
            //   ` //++++++++++++++++++++++++++++++++++++
            //                           catching unhandled hub events
            //                           //++++++++++++++++++++++++++++++++++++`,
            //   event,
            //   indexPoint
            // );

            // eventHTML = eventHTML.push((processOtherEvent(event, indexPoint)));
            break;
          case "info":
            // indexPoint = indexPoint + 1;
            // console.log(
            //   `
            //                           //++++++++++++++++++++++++++++++++++++
            //                           catching unhandled info events
            //                           //++++++++++++++++++++++++++++++++++++`,
            //   event.component,
            //   indexPoint
            // );
            //eventHTML = eventHTML.push((signatureEvent(event, indexPoint)));
            // eventHTML = eventHTML.push((processOtherEvent(event, indexPoint)));
            break;
          case "signature":
            indexPoint = indexPoint + 1;
            questionEvent = {
              componentType: event.component.type,
              componentClass: "hot-pink",
              id: event.component.id,
              questionPrompt: event.component.prompt,
              answer: event.answer.signature, // or null
              commentsCaptionsPhotos: event.answer.signature,
              processedAt: event.processedAt.seconds,
              userName: event.user.name,
              latitude:
                event.location != undefined ? event.location.latitude : "",
              longitude:
                event.location != undefined ? event.location.longitude : "",
              deviceManufacturer: event.device.manufacturer,
              deviceModel: event.device.model,
              devicePlatform: event.device.platform,
              deviceVersion: event.device.version,
              appVersion: event.device.appVersion,
            };
            eventHTML.push({
              component: (
                <SignatureEvent
                  data={questionEvent}
                  indexPoint={indexPoint}
                  setCenter={setCenter}
                  setHighlighted={setHighlighted}
                />
              ),
              indexPoint,
              event: questionEvent,
            });
            break;
          case "scan":
            indexPoint = indexPoint + 1;
            questionEvent = {
              componentType: event.component.type,
              componentClass: "mint",
              id: event.component.id,
              questionPrompt: event.component.prompt,
              answer: event.answer.subject.name, // or null
              commentsCaptionsPhotos: event.answer,
              processedAt: event.processedAt.seconds,
              userName: event.user.name,
              latitude:
                event.location != undefined ? event.location.latitude : "",
              longitude:
                event.location != undefined ? event.location.longitude : "",
              deviceManufacturer: event.device.manufacturer,
              deviceModel: event.device.model,
              devicePlatform: event.device.platform,
              deviceVersion: event.device.version,
              appVersion: event.device.appVersion,
            };
            eventHTML.push({
              component: (
                <ScanEvent
                  data={questionEvent}
                  indexPoint={indexPoint}
                  setCenter={setCenter}
                  setHighlighted={setHighlighted}
                />
              ),
              indexPoint,
              event: questionEvent,
            });
            break;
          // case "picture":
          //   indexPoint = indexPoint + 1;
          //   // console.log(
          //   //   `
          //   //                           //++++++++++++++++++++++++++++++++++++
          //   //                           catching unhandled picture events
          //   //                           //++++++++++++++++++++++++++++++++++++`,
          //   //   event,
          //   //   indexPoint
          //   // );
          //   eventHTML = eventHTML.push((pictureEvent(event, indexPoint)));
          //   break;
          case "media":
            indexPoint = indexPoint + 1;
            questionEvent = {
              componentType: event.component.type,
              componentClass: "pink",
              id: event.component.id,
              questionPrompt: event.component.prompt,
              answer: event.answer.video,
              commentsCaptionsPhotos: event.answer,
              processedAt: event.processedAt.seconds,
              userName: event.user.name,
              latitude:
                event.location != undefined ? event.location.latitude : "",
              longitude:
                event.location != undefined ? event.location.longitude : "",
              deviceManufacturer: event.device.manufacturer,
              deviceModel: event.device.model,
              devicePlatform: event.device.platform,
              deviceVersion: event.device.version,
              appVersion: event.device.appVersion,
            };
            eventHTML.push({
              component: (
                <MediaEvent
                  data={questionEvent}
                  indexPoint={indexPoint}
                  setCenter={setCenter}
                  setHighlighted={setHighlighted}
                />
              ),
              indexPoint,
              event: questionEvent,
            });
            break;
          // case "multiSelect":
          // indexPoint = indexPoint + 1;
          // eventHTML = eventHTML + multiSelectEvent(event, indexPoint)
          // console.log("multiSelect Event", event)
          case "end":
            indexPoint = indexPoint + 1;
            questionEvent = {
              componentType: event.component.type,
              componentClass: "red",
              id: event.component.id,
              questionPrompt: null,
              commentsCaptionsPhotos: {},
              startedAt: convertDate(
                event.processedAt.seconds,
                "shortDateTime",
              ),
              startedBy: event.user.name,
              processedAt: event.processedAt.seconds,
              userName: event.user.name,
              latitude:
                event.location != undefined ? event.location.latitude : "",
              longitude:
                event.location != undefined ? event.location.longitude : "",
              deviceManufacturer: event.device.manufacturer,
              deviceModel: event.device.model,
              devicePlatform: event.device.platform,
              deviceVersion: event.device.version,
              appVersion: event.device.appVersion,
            };
            eventHTML.push({
              component: (
                <EndEvent
                  data={questionEvent}
                  indexPoint={indexPoint}
                  setCenter={setCenter}
                  setHighlighted={setHighlighted}
                />
              ),
              indexPoint,
              event: questionEvent,
            });
            break;
          default:
            console.log(`
                          //++++++++++++++++++++++++++++++++++++
                          catching unhandled events, ${event.component.type}
                          //++++++++++++++++++++++++++++++++++++`);
        }

        if (eventHTML.length > previousEventCount) {
          eventHTML[eventHTML.length - 1].sourceEventId =
            event.id ?? event.component?.id;
        }

        // if (sortedArrayLength == indexPoint) {
        //   lastElementID = event.component.id;
        // }
      });

      setTaskEvents(eventHTML);

      // if(doc.data().obsoleted){

      //   console.log("doc.data().obsoleted", doc.data().obsoleted);

      //   let obsoleted = doc.data().obsoleted;
      //   taskStatus.classList.remove("task-status-live");
      //   taskStatusIndicator.style.display = "none";
      //   taskStatus.innerText = "Task Obsoleted";
      //   obsoleteMenuItem.style.display = "none";

      //   let osboleteEventWrapper = document.createElement("DIV");
      //   osboleteEventWrapper.classList.add("event-wrapper", "answer-panel");
      //   console.log("obsoleted.at", obsoleted.at)
      //   let obsoletedAt = luxon.DateTime.fromSeconds(obsoleted.at.seconds).toFormat("DD HH:mm:ss ");
      //   let obsoleteHTML = `
      //   <div className={classes.event card}>
      //       <div className={classes.eventHeader}>
      //           <div className={classes.responseWrapper}>
      //               <div className={classes.response}><div className={classes.question}>TASK OBSOLETE:</div></div>
      //               <div className={classes.response}><div className={classes.answerBody}>${obsoleted.reason}</div></div>
      //               <div className={classes.response}><div className={classes.question}>Obsoleted By:</div><div className={classes.answerBody}>${obsoleted.by.name}</div></div>
      //               <div className={classes.response}><div className={classes.question}>Obsoleted At:</div><div className={classes.answerBody}>${obsoletedAt}</div></div>
      //           </div>
      //       </div>
      //   </div>
      //   <div className={classes.eventAux}>
      //       <div className={classe.eventTime + classes.obsolete}>
      //       OBSOLETE
      //       </div>
      //       <div className={classes.eventIcon + classes.red} id="8278111d-28be-4f16-963b-d743bc34521c">
      //           <svg xmlns="http://www.w3.org/2000/svg" fill="#ffffff" width="18px" height="18px" viewBox="0 0 24 24"><title>pencil-off-outline</title><path fill="#fff;" d="M10,10.2L14,6.2L17.8,10L13.8,14L12.4,12.6L15,9.9L14.1,9L11.5,11.6L10,10.2M20.7,5.6L18.4,3.3C18.2,3.1 17.9,3 17.7,3C17.5,3 17.2,3.1 17,3.3L15.2,5.1L19,8.9L20.7,7C21.1,6.7 21.1,6 20.7,5.6M19,21.7L17.7,23L11.2,16.5L6.8,21H3V17.2L7.5,12.7L1,6.3L2.3,5L19,21.7M9.8,15.1L8.9,14.2L5,18.1V19H5.9L9.8,15.1Z" /></svg>
      //           <div className={classes.iconLable}>obsolete</div>
      //       </div>
      //   </div>`

      //   osboleteEventWrapper.innerHTML = obsoleteHTML;
      //   container.appendChild(osboleteEventWrapper);

      //   // console.log("obsoleteDiv", obsoleteEventDiv);
      //   // console.log("container", container);

      // }

      // console.log("Last element event ID", lastElementID);
      // let scrollTarget = document.getElementById(lastElementID);
      // // console.log("scrollTarget", scrollTarget);
      // if (scrollTarget) {
      //   scrollTarget.scrollIntoView({ behavior: "smooth" });
      // }
      // handleDetailPanels();
      // handleEventIcons();
      // handlePhotos();
      // startCounter();
    });

    return () => {
      unsub();
    };
  }, []);
  return (
    <TaskImageLightboxContext.Provider
      value={(image) => setLightboxImage({ images: [image], index: 0 })}
    >
      <>
      <Breadcrumbs className={classes.print} breadcrumbs={pageBreadcrumbs} rightslot={ allowed &&
        <>
          <div
              className={`px-4 py-1 rounded-full text-white text-sm flex items-center gap-2 ${
                isObsoleted
                  ? "bg-red-600"
                  : hasEndEvent
                    ? "bg-slate-600"
                    : "bg-amber-500"
              }`}
            >
              {!isObsoleted && !hasEndEvent ? (
                <svg
                  className="w-4 h-4 animate-pulse"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M11 15H6L13 1V9H18L11 23V15Z" />
                </svg>
              ) : null}
              {statusText}
            </div>

            <button
              type="button"
              className="w-8 h-8 rounded bg-white border border-slate-300 flex items-center justify-center"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="Task actions"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="#111827">
                <path d="M12,16A2,2 0 1,1 10,18A2,2 0 0,1 12,16M12,10A2,2 0 1,1 10,12A2,2 0 0,1 12,10M12,4A2,2 0 1,1 10,6A2,2 0 0,1 12,4Z" />
              </svg>
            </button>

            {menuOpen ? (
              <div className="absolute top-12 right-4 bg-white border border-slate-200 rounded shadow-md z-200 min-w-[12rem]">
                <button
                  className="w-full text-left px-4 py-3 hover:bg-slate-50 border-b"
                  onClick={onPrintTask}
                >
                  Print Task
                </button>
                {!isObsoleted ? (
                  <button
                    type="button"
                    className="w-full text-left px-4 py-3 hover:bg-slate-50"
                    onClick={onOpenObsolete}
                  >
                    Obsolete Task
                  </button>
                ) : null}
              </div>
            ) : null}
          </>
      }/> 
      {allowed ?(<>
      <div className="flex flex-col lg:flex-row w-full">
        <div className="w-full lg:min-w-[40%] h-[100vh] overflow-scroll">
          <h1 style={{ margin: 0, padding:"1rem 0 0 0" }}>{tasksWorkflowName}</h1>
          {taskEvents.map((item) => {
            const hasSourceEventId =
              item.sourceEventId !== undefined &&
              item.sourceEventId !== null &&
              item.sourceEventId !== "";

            return (
              <div
                className={classes.eventScrollTarget}
                data-event-id={hasSourceEventId ? item.sourceEventId : undefined}
                id={
                  hasSourceEventId
                    ? getTaskEventElementId(item.sourceEventId)
                    : undefined
                }
                key={
                  hasSourceEventId
                    ? item.sourceEventId
                    : `${item.event.id}-${item.indexPoint}`
                }
              >
                <TaskImageLightboxContext.Provider
                  value={(image) => openEventImage(item.event, image)}
                >
                  {item.component}
                </TaskImageLightboxContext.Provider>
              </div>
            );
          })}
        </div>
        <div className={`hidden lg:block h-[100vh] lg:min-w-[60%] ${classes.print}`} >
          {locatAvailable ? (
            <Map
              defaultZoom={20}
              defaultCenter={{
                lat: taskEvents[0]?.event.latitude ?? 0,
                lng: taskEvents[0]?.event.longitude ?? 0,
                altitude: 1000,
              }}
              defaultTilt={45}
              onCameraChanged={(ev) => {
                console.log(
                  "camera changed:",
                  ev.detail.center,
                  "zoom:",
                  ev.detail.zoom,
                );
                setCenter(ev.detail.center);
              }}
              center={center}
              mapId={"3a1de68f53503ad2"}

            >
              {taskEvents.map((i) => {
                console.log("location", i.event.location, i);
                return (
                  i.event.latitude != undefined &&
                  i.event.longitude != undefined &&
                  typeof i.event.longitude == "number" &&
                  typeof i.event.latitude == "number" && (
                    <AdvancedMarker
                      key={i.event.id}
                      position={{
                        lat: i.event.latitude,
                        lng: i.event.longitude,
                      }}
                      zIndex={highlighted === i.indexPoint ? 10000 : 1}
                    >
                      <BuildContent
                        eventData={i.event}
                        highlighted={highlighted == i.indexPoint}
                        indexPoint={i.indexPoint}
                        setHighlighted={setHighlighted}
                      />
                    </AdvancedMarker>
                  )
                );
              })}
            </Map>
          ) : (
            <div className={classes["no-map-warning"]}>
              <div>Unable to Map Event Geolocations</div>
              <h1>Device Location Disabled By User</h1>
            </div>
          )}
        </div>
      </div>
      {obsoleteOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center">
          <div className="bg-white rounded shadow-xl w-full max-w-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-medium">Obsolete Task</h2>
              <button type="button" onClick={() => setObsoleteOpen(false)}>
                Close
              </button>
            </div>
            <label className="block text-sm mb-2">Reason for obsoletion</label>
            <textarea
              value={obsoleteReason}
              onChange={(e) => setObsoleteReason(e.target.value)}
              placeholder="Please describe why you are obsoleting this task"
              rows={4}
              className="w-full border rounded px-3 py-2 text-sm"
            />
            <div className="flex justify-end mt-4 gap-2">
              <button
                type="button"
                className="px-3 py-2 border rounded"
                onClick={() => setObsoleteOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-3 py-2 bg-red-600 text-white rounded disabled:opacity-50"
                onClick={onConfirmObsolete}
                disabled={!obsoleteReason.trim() || obsoleting}
              >
                {obsoleting ? "Obsoleting..." : "Obsolete Task"}
              </button>
            </div>
          </div>
        </div>
      )}
      </>) : (            
            <section className=" mx-auto mt-[100] flex flex-col items-center mb-[32px] bg-white rounded-xl overflow-hidden py-[32px] px-[32px] shadow-lg w-full md:w-[800px]">

              <div className="flex gap-[16px] w-full justify-start items-center mb-[32px]">
                  <div className="bg-sky-500 p-[8px] flex justify-center items-center rounded-[999px]">
                    <svg width="24px" height="24px"  viewBox="0 0 24 24"><title>security</title><path fill="#FFFFFF" d="M12,12H19C18.47,16.11 15.72,19.78 12,20.92V12H5V6.3L12,3.19M12,1L3,5V11C3,16.55 6.84,21.73 12,23C17.16,21.73 21,16.55 21,11V5L12,1Z" /></svg>
                  </div>

                  <h3 className="sub-title w-full text-left mb-[0px]">Insufficient Access Rights!</h3>
              </div>
              <p className="text-left text-[#4C4C4C] pb-[64px]">
                Sorry, you don’t have permission to view this data on this account.
                Please contact your internal account manager to request access permissions.
                <br/>
                <br/>
                Once access has been granted you will need to log out and log in
                again to gain access.
              </p>
            </section>)}
      <TaskImageLightbox
        image={
          lightboxImage
            ? {
                ...lightboxImage,
                setIndex: (updateIndex) =>
                  setLightboxImage((current) =>
                    current
                      ? {
                          ...current,
                          index:
                            typeof updateIndex === "function"
                              ? updateIndex(current.index)
                              : updateIndex,
                        }
                      : current,
                  ),
              }
            : null
        }
        onClose={() => setLightboxImage(null)}
      />
      </>
    </TaskImageLightboxContext.Provider>
  );
}
